import * as React from "react";
import { useDragCallbacks } from "./drag-interaction-utils";
import { SwipeStackStyle } from "./swipe-stack-style";

interface Props {
    readonly frontKey: string;
    readonly rearKey?: string;
    readonly frontContents: React.ReactNode;
    readonly rearContents?: React.ReactNode;
    readonly isEnabled: boolean;
    readonly onLeft?: () => void;
    readonly onRight?: () => void;
}

const minMotion = 75;

const SwipeStack: React.FunctionComponent<Props> = p => {
    const { onLeft, onRight, frontContents, rearContents, isEnabled, frontKey, rearKey } = p;

    const [xOffset, setXOffset] = React.useState(0);

    const onMove = React.useCallback(
        (_, [dx]) => {
            if (!isEnabled) return;

            setXOffset(dx);
        },
        [isEnabled]
    );

    React.useEffect(() => {
        setXOffset(0);
    }, [frontKey]);

    const exit = React.useCallback(
        (end: "right" | "left" | "center") => {
            let val = xOffset;
            let last: number | undefined = undefined;
            const cb = (timestamp: number) => {
                if (last === undefined) {
                    last = timestamp - 16;
                }
                const step = timestamp - last;
                last = timestamp;
                if (end === "center") {
                    val = (Math.max(0, Math.abs(val) - step * 2) * val) / Math.abs(val);
                } else {
                    val += step * 2 * (end === "right" ? 1 : -1);
                }
                setXOffset(val);

                if (end === "right" && onRight !== undefined) {
                    if (val < 500) {
                        window.requestAnimationFrame(cb);
                    } else {
                        onRight();
                    }
                } else if (end === "left" && onLeft !== undefined) {
                    if (val > -500) {
                        window.requestAnimationFrame(cb);
                    } else {
                        onLeft();
                    }
                } else {
                    if (val !== 0) {
                        window.requestAnimationFrame(cb);
                    } else {
                        setXOffset(0);
                    }
                }
            };

            window.requestAnimationFrame(cb);
        },
        [onRight, onLeft, xOffset]
    );

    const onEnd = React.useCallback(() => {
        if (!isEnabled) return;

        if (xOffset > minMotion) {
            exit("right");
        } else if (xOffset < -minMotion) {
            exit("left");
        } else {
            exit("center");
        }
    }, [exit, xOffset, isEnabled]);

    const callbacks = useDragCallbacks(undefined, onMove, onEnd);

    const frontStyle = React.useMemo((): React.CSSProperties => {
        return {
            transform: `translateX(${xOffset}px) rotate(${xOffset / 20}deg)`,
        };
    }, [xOffset]);

    const rearStyle = React.useMemo((): React.CSSProperties => {
        return {
            transform: `scale(${0.9 + 0.1 * (Math.abs(xOffset) / 500)})`,
        };
    }, [xOffset]);

    return (
        <SwipeStackStyle {...callbacks}>
            <div className="grid">
                <div key={frontKey} className="card front" style={frontStyle}>
                    {frontContents}
                </div>
                <div key={rearKey} className="card rear" style={rearStyle}>
                    {rearContents}
                </div>
            </div>
        </SwipeStackStyle>
    );
};

export default SwipeStack;
